'use client'

import Link from 'next/link'
import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import type { Session } from 'next-auth'
import { useCart } from '@/contexts/CartContext'
import { cartCount } from '@/lib/cart'
import { pluralPl } from '@/lib/format'
import { SearchModal } from './SearchModal'
import { logout } from '@/app/actions/auth'
import type { NavCategory } from '@/lib/nav-categories'

// Linki pomocnicze (cienki górny pasek na desktopie + sekcja w menu mobilnym).
const utilityLinks = [
  { label: 'O nas', href: '/o-nas' },
  { label: 'Dostawa i zwroty', href: '/dostawa-i-zwroty' },
  { label: 'Kontakt', href: '/kontakt' },
]

// Płaskie linki obok dyscyplin w głównym pasku (Marki tylko w stopce /katalog/marki — oszczędność miejsca).
const flatLinks = [
  { label: 'Poradniki', href: '/poradniki' },
]

function UserGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

interface HeaderProps {
  session: Session | null
  categories: NavCategory[]
}

export function Header({ session, categories }: HeaderProps) {
  const [open, setOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isMac, setIsMac] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null) // mega-menu desktop (slug dyscypliny)
  const [hoveredSub, setHoveredSub] = useState<string | null>(null) // podświetlona podkat. (podgląd zdjęcia)
  const [mobileCat, setMobileCat] = useState<string | null>(null) // akordeon mobilny
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const { cart } = useCart()
  const count = cartCount(cart.items)

  useEffect(() => {
    // navigator is browser-only and unavailable during SSR, so this must run after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMac(/mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent))
  }, [])

  const shortcutLabel = isMac ? 'Cmd+K' : 'Ctrl+K'

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // toLowerCase: z CapsLock/Shift e.key to 'K' — skrót ma działać niezależnie.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
      if (e.key === 'Escape') {
        setOpen(false)
        setOpenMenu(null)
        setUserMenuOpen(false)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  useEffect(() => {
    function handleScroll() { setScrolled(window.scrollY > 20) }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!userMenuOpen) return
    function handlePointerDown(e: PointerEvent) {
      if (!userMenuRef.current?.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [userMenuOpen])

  function canHover() {
    return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  }

  // Dyscyplina aktywna gdy jesteśmy na jej stronie, jej produktach lub jej podkategoriach.
  function isCatActive(cat: NavCategory): boolean {
    const base = `/katalog/${cat.slug}`
    if (pathname === base || pathname.startsWith(`${base}/`)) return true
    return cat.children.some((c) => pathname === `/katalog/${c.slug}` || pathname.startsWith(`/katalog/${c.slug}/`))
  }

  return (
    <>
    <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    <header
      className="sticky top-0 z-50 border-b transition-shadow duration-300"
      style={{
        backgroundColor: '#03080F',
        borderColor: 'rgba(255,255,255,0.08)',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.5)' : 'none',
      }}
    >
      {/* Cienki górny pasek pomocniczy — tylko desktop (lg+) */}
      <div className="hidden lg:block border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-9 text-xs">
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>
              Sprzęt do sportów podwodnych z opisami po polsku
            </span>
            <nav className="flex items-center gap-5" aria-label="Pomocnicze">
              {utilityLinks.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  className="transition-colors hover:text-white"
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" aria-label="Strona główna divemate" className="flex items-center transition-opacity hover:opacity-80 shrink-0">
            <span
              className="text-xl font-black tracking-tight"
              style={{ fontFamily: 'var(--font-heading)', color: '#FFFFFF' }}
            >
              dive<span style={{ color: '#1FB8B0' }}>mate</span>
            </span>
          </Link>

          {/* Główna nawigacja z mega-menu — desktop (lg+) */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Kategorie">
            {categories.map((cat) => {
              const active = isCatActive(cat)
              const isOpen = openMenu === cat.slug
              const hasChildren = cat.children.length > 0
              return (
                <div
                  key={cat.slug}
                  className="relative"
                  onMouseEnter={() => { setOpenMenu(cat.slug); setHoveredSub(null) }}
                  onMouseLeave={() => { setOpenMenu(null); setHoveredSub(null) }}
                  onBlur={(e) => {
                    // Zamknij mega-menu gdy fokus (Tab) wychodzi poza kontener dyscypliny —
                    // onFocus otwierał, ale nic nie zamykało przy nawigacji klawiaturą.
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      setOpenMenu(null)
                      setHoveredSub(null)
                    }
                  }}
                >
                  <Link
                    href={`/katalog/${cat.slug}`}
                    aria-current={active ? 'page' : undefined}
                    aria-expanded={hasChildren ? isOpen : undefined}
                    onFocus={() => setOpenMenu(cat.slug)}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-white hover:bg-white/5"
                    style={{ color: active ? '#1FB8B0' : 'rgba(255,255,255,0.7)' }}
                  >
                    {cat.name}
                    {hasChildren && (
                      <svg
                        width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                        className="transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'none', opacity: 0.6 }}
                      >
                        <path d="m5 7 5 5 5-5" />
                      </svg>
                    )}
                  </Link>

                  {hasChildren && isOpen && (() => {
                    // Podgląd zdjęcia po prawej (jak na x-kom): podświetlona podkat. lub
                    // pierwsza z dostępnym zdjęciem jako domyślna.
                    const withImg = cat.children.filter((s) => s.image)
                    const preview = cat.children.find((s) => s.slug === hoveredSub && s.image) ?? withImg[0] ?? null
                    return (
                    // pt-3 tworzy „mostek" hover między triggerem a panelem (brak luki).
                    <div className="absolute left-0 top-full pt-3 z-50">
                      <div
                        className="rounded-xl border p-3 shadow-2xl flex gap-3"
                        style={{ backgroundColor: '#0A1422', borderColor: 'rgba(255,255,255,0.1)' }}
                      >
                        <div style={{ minWidth: '22rem' }}>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                            {cat.children.map((sub) => {
                              const highlighted = preview?.slug === sub.slug
                              return (
                                <Link
                                  key={sub.slug}
                                  href={`/katalog/${sub.slug}`}
                                  onMouseEnter={() => setHoveredSub(sub.slug)}
                                  onFocus={() => setHoveredSub(sub.slug)}
                                  className="px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/5 hover:text-white"
                                  style={{ color: highlighted ? '#FFFFFF' : 'rgba(255,255,255,0.65)', backgroundColor: highlighted ? 'rgba(255,255,255,0.05)' : 'transparent' }}
                                >
                                  {sub.name}
                                </Link>
                              )
                            })}
                          </div>
                          <Link
                            href={`/katalog/${cat.slug}`}
                            className="mt-2 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors hover:bg-white/5"
                            style={{ color: '#1FB8B0', borderTop: '1px solid rgba(255,255,255,0.08)' }}
                          >
                            Wszystko w „{cat.name}”
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                          </Link>
                        </div>

                        {/* Prawy panel: podgląd zdjęcia reprezentacyjnego podkategorii */}
                        {preview && (
                          <Link
                            href={`/katalog/${preview.slug}`}
                            onMouseEnter={() => setHoveredSub(preview.slug)}
                            className="hidden xl:flex flex-col w-48 shrink-0 rounded-lg overflow-hidden group"
                            style={{ backgroundColor: '#07111F', border: '1px solid rgba(255,255,255,0.07)' }}
                            aria-hidden="true"
                            tabIndex={-1}
                          >
                            <div className="aspect-square overflow-hidden" style={{ backgroundColor: '#0A1828' }}>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={preview.image!}
                                alt={preview.name}
                                width={192}
                                height={192}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            </div>
                            <span className="px-3 py-2.5 text-sm font-medium transition-colors group-hover:text-white" style={{ color: 'rgba(255,255,255,0.8)' }}>
                              {preview.name}
                            </span>
                          </Link>
                        )}
                      </div>
                    </div>
                    )
                  })()}
                </div>
              )
            })}

            {flatLinks.map((link) => {
              const isActive = pathname.startsWith(link.href)
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-white hover:bg-white/5"
                  style={{ color: isActive ? '#1FB8B0' : 'rgba(255,255,255,0.7)' }}
                >
                  {link.label}
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-white/5"
              style={{ color: 'rgba(255,255,255,0.5)' }}
              aria-label={`Szukaj (${shortcutLabel})`}
              title={`Szukaj (${shortcutLabel})`}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="9" cy="9" r="6" />
                <path d="m15 15 3 3" />
              </svg>
            </button>

            <Link
              href="/koszyk"
              className="relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-white/5"
              style={{ color: 'rgba(255,255,255,0.5)' }}
              aria-label="Koszyk"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: '#FF6B47', color: '#03080F' }}
                  role="status"
                  aria-live="polite"
                  aria-label={`${count > 9 ? 'Więcej niż 9' : count} ${pluralPl(count, ['produkt', 'produkty', 'produktów'])} w koszyku`}
                >
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </Link>

            {session ? (
              <div
                ref={userMenuRef}
                className="hidden lg:block relative"
                onMouseEnter={() => { if (canHover()) setUserMenuOpen(true) }}
                onMouseLeave={() => { if (canHover()) setUserMenuOpen(false) }}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    setUserMenuOpen(false)
                  }
                }}
              >
                <button
                  type="button"
                  className="flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-white/5 hover:text-white"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="header-user-menu"
                  aria-label={`Konto: ${session.user?.name ?? 'użytkownik'}`}
                  onClick={(e) => {
                    // Hover already opened the menu; a mouse click would otherwise toggle it shut.
                    // Keyboard (detail 0) and touch (no hover) still toggle. Escape / leave / outside close.
                    const fromKeyboard = e.detail === 0
                    if (!fromKeyboard && canHover()) {
                      setUserMenuOpen(true)
                      return
                    }
                    setUserMenuOpen((prev) => !prev)
                  }}
                >
                  {session.user?.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={session.user.image}
                      alt=""
                      width={28}
                      height={28}
                      decoding="async"
                      className="w-7 h-7 rounded-full border shrink-0"
                      style={{ borderColor: 'rgba(255,255,255,0.15)' }}
                    />
                  ) : (
                    <UserGlyph />
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full pt-2 z-50">
                    <div
                      id="header-user-menu"
                      role="menu"
                      className="min-w-[11rem] rounded-xl border py-1.5 shadow-2xl"
                      style={{ backgroundColor: '#0A1422', borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <Link
                        href="/konto/zamowienia"
                        role="menuitem"
                        className="block px-3 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
                        style={{ color: 'rgba(255,255,255,0.75)' }}
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Moje zamówienia
                      </Link>
                      <form action={logout}>
                        <button
                          type="submit"
                          role="menuitem"
                          className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
                          style={{ color: 'rgba(255,255,255,0.55)' }}
                        >
                          Wyloguj
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="hidden lg:block text-sm font-medium px-2 py-2 rounded-lg transition-colors hover:bg-white/5 hover:text-white"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                Zaloguj się
              </Link>
            )}
            <button
              className="lg:hidden p-3 rounded"
              style={{ color: 'rgba(255,255,255,0.5)' }}
              onClick={() => setOpen(!open)}
              aria-label="Menu nawigacyjne"
              aria-expanded={open}
              aria-haspopup="menu"
            >
              {open ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Menu mobilne (< lg) z akordeonem dyscyplin */}
      {open && (
        <div
          className="mobile-nav lg:hidden border-t px-4 py-3 max-h-[calc(100vh-4rem)] overflow-y-auto"
          style={{ backgroundColor: '#03080F', borderColor: 'rgba(255,255,255,0.08)' }}
        >
          {categories.map((cat) => {
            const expanded = mobileCat === cat.slug
            const hasChildren = cat.children.length > 0
            return (
              <div key={cat.slug} className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between">
                  <Link
                    href={`/katalog/${cat.slug}`}
                    className="flex-1 py-3 text-sm font-semibold transition-colors hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.85)' }}
                    onClick={() => setOpen(false)}
                  >
                    {cat.name}
                  </Link>
                  {hasChildren && (
                    <button
                      onClick={() => setMobileCat(expanded ? null : cat.slug)}
                      className="p-2 -mr-2"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                      aria-label={expanded ? `Zwiń ${cat.name}` : `Rozwiń ${cat.name}`}
                      aria-expanded={expanded}
                    >
                      <svg
                        width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                        className="transition-transform duration-200" style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
                      >
                        <path d="m5 7 5 5 5-5" />
                      </svg>
                    </button>
                  )}
                </div>
                {hasChildren && expanded && (
                  <div className="grid grid-cols-2 gap-x-2 pb-3 pl-1">
                    {cat.children.map((sub) => (
                      <Link
                        key={sub.slug}
                        href={`/katalog/${sub.slug}`}
                        className="py-1.5 text-sm transition-colors hover:text-white"
                        style={{ color: 'rgba(255,255,255,0.6)' }}
                        onClick={() => setOpen(false)}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          <div className="pt-3 space-y-1">
            {[...flatLinks, ...utilityLinks].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block py-2 text-sm font-medium transition-colors hover:text-white"
                style={{ color: 'rgba(255,255,255,0.7)' }}
                onClick={() => setOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="pt-3 mt-2 border-t space-y-1" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <Link
              href="/koszyk"
              className="block py-2 text-sm font-medium transition-colors hover:text-white"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              onClick={() => setOpen(false)}
            >
              Koszyk {count > 0 && `(${count})`}
            </Link>
            {session ? (
              <>
                <Link
                  href="/konto/zamowienia"
                  className="block py-2 text-sm font-medium"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                  onClick={() => setOpen(false)}
                >
                  Moje zamówienia
                </Link>
                <form action={logout}>
                  <button type="submit" className="block py-2 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    Wyloguj
                  </button>
                </form>
              </>
            ) : (
              <Link
                href="/login"
                className="block py-2 text-sm font-medium"
                style={{ color: 'rgba(255,255,255,0.7)' }}
                onClick={() => setOpen(false)}
              >
                Zaloguj się
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  )
}
